/* eslint-disable max-lines */
import {
  useArtworkForm,
  usePlacementPosition,
  mobileSizePlacementOpenAtom
} from "@design-lab/entities/artwork";
import { atom, useAtom, useSetAtom } from "jotai";
import { CloudUpload, PaintBucket, Palette, Ruler, Shirt } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import { useCurrentComposition } from "@design-lab/entities/composition";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { trackTabNavigation } from "@design-lab/shared/lib/analytics";
import { useShowWeaveDesignLab, useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { getPath } from "common/utils/ui/use_svg";
import type { printOnDemand } from "common/utils/models";

export type Tab = "upload" | "products" | "size" | "front" | "back" | "design";
export type DesignSubTab = "front" | "back";

type SurfaceMetaSides = printOnDemand.getProduct.SurfaceMeta["sides"];

type AppTabValidCallback = (
  params: ReturnType<typeof useValidation> & {
    sides?: SurfaceMetaSides;
  }
) => boolean;

type AppTabCanBeSkippedCallback = (params: {
  sides?: SurfaceMetaSides | undefined;
  isDesktop?: boolean;
}) => boolean;

export type AppTab = {
  title: string;
  mobileTitle: string;
  mobileAccordionTitle: string;
  value: Tab;
  canBeSkipped: AppTabCanBeSkippedCallback;
  icon: React.ReactNode;
  valid?: boolean;
  enabled?: boolean;
  checked?: boolean;
  isValid?: AppTabValidCallback;
  isChecked?: AppTabValidCallback;
  isEnabled?: AppTabValidCallback;
};

const tabInfoExpandedAtom = atom<boolean | undefined>(undefined);

// Atom to track active sub-tab within the Design tab (for A/B test variant)
export const designSubTabAtom = atom<DesignSubTab>("front");

// Atom to track if design sub-tab has been initialized
export const designSubTabInitializedAtom = atom<boolean>(false);

// useVisitedTabs is provided from shared/hooks to avoid circular import

export const APP_TABS_BASE: AppTab[] = [
  {
    title: "Upload",
    mobileTitle: "Upload",
    mobileAccordionTitle: "Upload",
    value: "upload",
    canBeSkipped: () => false,
    icon: <CloudUpload className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isFileSelected }) => isFileSelected,
    isChecked: ({ isFileSelected }) => isFileSelected
  },
  {
    title: "Products",
    mobileTitle: "Products",
    mobileAccordionTitle: "Modify Products & Colors",
    value: "products",
    canBeSkipped: () => false,
    icon: <PaintBucket className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: () => true,
    isChecked: ({ visitedTabs }) => visitedTabs.has("products")
  },
  {
    title: "Select Sizes",
    mobileTitle: "Sizes",
    mobileAccordionTitle: "Edit Sizes",
    value: "size",
    canBeSkipped: () => false,
    icon: <Ruler className="size-6" />,
    isEnabled: ({ isFileSelected }) => isFileSelected,
    isValid: ({ isQuantityValid }) => isQuantityValid,
    isChecked: ({ isQuantityValid }) => isQuantityValid
  },
  {
    title: "Design Front",
    mobileTitle: "Front",
    mobileAccordionTitle: "Design Front",
    value: "front",
    canBeSkipped: params => !params.isDesktop && Boolean(params.sides?.front?.length),
    icon: <Shirt className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isAtLeastOneArtworkProcessed }) => isAtLeastOneArtworkProcessed,
    isChecked: ({ visitedTabs, isFrontArtworkUploaded }) =>
      visitedTabs.has("front") && isFrontArtworkUploaded
  },
  {
    title: "Design Back",
    mobileTitle: "Back",
    mobileAccordionTitle: "Design Back",
    value: "back",
    canBeSkipped: params => !params.isDesktop && Boolean(params.sides?.back?.length),
    icon: <Shirt className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isAtLeastOneArtworkProcessed }) => isAtLeastOneArtworkProcessed,
    isChecked: ({ visitedTabs, isBackArtworkUploaded }) =>
      visitedTabs.has("back") && isBackArtworkUploaded
  },
  {
    title: "Design",
    mobileTitle: "Design",
    mobileAccordionTitle: "Design",
    value: "design",
    canBeSkipped: () => false,
    icon: <Palette className="size-6" />,
    isEnabled: () => true,
    isValid: ({ isAtLeastOneArtworkProcessed }) => isAtLeastOneArtworkProcessed,
    isChecked: ({ visitedTabs, isFrontArtworkUploaded, isBackArtworkUploaded }) =>
      (visitedTabs.has("front") || visitedTabs.has("design")) &&
      (isFrontArtworkUploaded || isBackArtworkUploaded)
  }
];

// Original order: Upload -> Products -> Size -> Front -> Back
const ORIGINAL_ORDER: Tab[] = ["upload", "products", "size", "front", "back"];

// New order for A/B test variant: Upload -> Design (consolidates Front+Back) -> Products -> Size
const REORDERED_ORDER: Tab[] = ["upload", "design", "products", "size"];

function getOrderedTabs(shouldReorder: boolean): AppTab[] {
  const order = shouldReorder ? REORDERED_ORDER : ORIGINAL_ORDER;
  const tabsMap = new Map(APP_TABS_BASE.map(tab => [tab.value, tab]));

  const ordered = order
    .map(value => tabsMap.get(value))
    .filter((tab): tab is AppTab => Boolean(tab));
  return ordered.length === order.length ? ordered : APP_TABS_BASE;
}

export function useAppTabs(): AppTab[] {
  const experiments = useExperiments();
  const experimentValue = experiments?.abDesignLabReorderTabs;
  const shouldReorder = shouldReorderDesignLabTabs(experimentValue);

  return useMemo(() => getOrderedTabs(shouldReorder), [shouldReorder]);
}

// Export APP_TABS for backward compatibility (will use original order)
// Components should use useAppTabs() hook instead
export const APP_TABS: AppTab[] = APP_TABS_BASE;

export function useTab() {
  const { lens, form: artworkForm } = useArtworkForm();
  const { isDesktop } = useResponsive();
  const currentPlacementLens = lens.focus("currentPlacement");
  const selectedFrontPlacementLens = lens.focus("selectedFrontPlacement");
  const currentPlacement = usePlacementPosition(currentPlacementLens);
  const selectedFrontPlacement = usePlacementPosition(selectedFrontPlacementLens);

  // Extract stable values to avoid re-renders
  const currentPlacementValue = currentPlacement.placement;
  const selectedFrontPlacementValue = selectedFrontPlacement.placement;
  const { setPlacement } = currentPlacement;

  const artworkFormValues = useWatch({ control: artworkForm.control });
  const validation = useValidation();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};
  const shouldShowWeaveDesignLab = useShowWeaveDesignLab();
  const setIsSizePlacementOpen = useSetAtom(mobileSizePlacementOpenAtom);
  const [activeSubTab] = useAtom(designSubTabAtom);
  const experiments = useExperiments();

  const showSubTabs = useMemo(
    () => shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs),
    [experiments?.abDesignLabReorderTabs]
  );

  const [isInfoExpanded, setIsInfoExpanded] = useAtom(tabInfoExpandedAtom);
  const {
    tab,
    setTab: setTabNav,
    currentIndex,
    nextTab,
    nextTabRaw,
    prevTab,
    prevTabRaw,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isProductsTab,
    isSizeTab,
    isSample
  } = useTabNavigation();

  const appTabs = useAppTabs();

  const tabs: AppTab[] = useMemo(() => {
    const validationWithSides = { ...validation, ...(sides && { sides }) };
    return appTabs.map(item => {
      let { title, mobileTitle, icon } = item;

      // Sample mode transformation
      if (isSample && item.value === "products") {
        title = "Colors";
        mobileTitle = "Colors";
      }

      // Create Artwork experiment transformation
      if (shouldShowWeaveDesignLab && item.value === "upload") {
        title = "Design";
        mobileTitle = "Design";
        icon = <Palette className="size-6" />;
      }

      // A/B test: Rename "Select Sizes" to "Sizes"
      if (showSubTabs && item.value === "size") {
        title = "Sizes";
      }

      // A/B test: Replace icons for design, sizes, and products tabs
      if (showSubTabs) {
        const ICON_META = {
          design: { base: "design", label: "Design" },
          size: { base: "sizes", label: "Sizes" },
          products: { base: "products", label: "Products" }
        } as const;

        if (item.value in ICON_META) {
          const isSelected = item.value === tab;
          const spritePath = getPath();
          const color = isSelected ? "#155DFC" : "#171717";
          const meta = ICON_META[item.value as keyof typeof ICON_META];
          const iconName = isSelected ? `${meta.base}-selected` : meta.base;

          icon = (
            <svg className="size-6" style={{ fill: "none", stroke: color }} aria-label={meta.label}>
              <use xlinkHref={`${spritePath}#${iconName}`} />
            </svg>
          );
        }
      }

      return {
        ...item,
        title,
        mobileTitle,
        icon,
        valid: item.isValid?.(validationWithSides) ?? false,
        checked: item.isChecked?.(validationWithSides) ?? false,
        enabled: item.isEnabled?.(validationWithSides) ?? false
      };
    });
  }, [appTabs, isSample, shouldShowWeaveDesignLab, validation, sides, showSubTabs, tab]);

  const currentTabFromTabs = tabs.find(t => t.value === tab);
  const isValid = currentTabFromTabs?.valid ?? tabs[currentIndex]?.valid;

  const setTab = (value: Tab) => {
    setTabNav(value);

    const hasFrontArtwork = !!artworkFormValues?.[selectedFrontPlacement.placement]?.artworkId;
    const hasBackArtwork = !!artworkFormValues?.back?.artworkId;
    const hasOnlyBack = hasBackArtwork && !hasFrontArtwork;
    const hasOnlyFront = hasFrontArtwork && !hasBackArtwork;

    let targetPlacement: "back" | typeof selectedFrontPlacement.placement | null = null;

    // Handle direct front/back tabs (both variants)
    if (value === "front") {
      targetPlacement = selectedFrontPlacement.placement;
    } else if (value === "back") {
      targetPlacement = "back";
    }
    // CANDIDATE VARIANT: Sync placement with activeSubTab for tabs with sub-tab UI
    // activeSubTab is global state tracking which sub-tab (front/back) is selected
    else if (showSubTabs) {
      if (value === "design" || value === "upload") {
        // Design/upload tabs have sub-tab UI - sync placement with activeSubTab
        targetPlacement = activeSubTab === "front" ? selectedFrontPlacement.placement : "back";
      }
    }
    // CONTROL VARIANT: Normal flow (Upload, Products, Size tabs,Front,Back)
    else {
      const isControlTab = value === "upload" || value === "products" || value === "size";

      if (isControlTab) {
        // Only change placement if artwork exists exclusively on one side
        if (hasOnlyFront) {
          targetPlacement = selectedFrontPlacement.placement;
        } else if (hasOnlyBack) {
          targetPlacement = "back";
        }
      }
    }

    if (targetPlacement) {
      currentPlacement.setPlacement(targetPlacement);
    }
  };

  // Only sync placement when tab or activeSubTab actually changes, not when placement changes
  const lastSyncedRef = useRef({ tab: "", activeSubTab: "" as DesignSubTab });

  useEffect(() => {
    // Check if tab or activeSubTab actually changed
    const hasChanged =
      lastSyncedRef.current.tab !== tab || lastSyncedRef.current.activeSubTab !== activeSubTab;

    if (!hasChanged) {
      return;
    }

    // Update ref to mark as processed
    lastSyncedRef.current = { tab, activeSubTab };

    // CANDIDATE VARIANT: Sync placement with activeSubTab on design/upload tabs
    if (showSubTabs && (tab === "design" || tab === "upload")) {
      const targetPlacement = activeSubTab === "front" ? selectedFrontPlacementValue : "back";

      if (currentPlacementValue !== targetPlacement) {
        setPlacement(targetPlacement);
      }
      return;
    }

    // CONTROL VARIANT: Sync placement for direct front/back tab navigation
    if (!showSubTabs) {
      if (tab === "back" && currentPlacementValue !== "back") {
        setPlacement("back");
      } else if (tab === "front" && currentPlacementValue === "back") {
        setPlacement(selectedFrontPlacementValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeSubTab, showSubTabs]);

  const next = () => {
    setIsSizePlacementOpen(false);
    if (nextTabRaw) {
      setTab(nextTabRaw.value);
      trackTabNavigation(nextTabRaw.value, "next");
    }
  };

  const back = () => {
    setIsSizePlacementOpen(false);
    if (prevTabRaw) {
      setTab(prevTabRaw.value);
      trackTabNavigation(prevTabRaw.value, "tabs");
    }
  };

  const toggleInfoExpanded = () => {
    setIsInfoExpanded(!isInfoExpanded);
  };

  useEffect(() => {
    if (isInfoExpanded === undefined) {
      setIsInfoExpanded(isDesktop);
    }
  }, [isDesktop, isInfoExpanded, setIsInfoExpanded]);

  return {
    tabs,
    tab,
    setTab,
    nextTab,
    prevTab,
    next,
    back,
    canBeSkipped,
    canGoNext,
    canGoBack,
    isDesignTab,
    isValid,
    isProductsTab,
    isSizeTab,
    isInfoExpanded,
    toggleInfoExpanded
  };
}
